import type { ArtistProfileResponse, SerializedProfileAsset } from 'fym-shared/src/music24/artistProfile';
import { drawnRemoteSrc, imgproxySrc } from '../../../../common/drawnImageSrc';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { profileFileUrl } from '../profileAssets';

/**
 * The thumbnail addresses of the Studio "Your profile" card (owner rule 2026-10-19: every drawn
 * image goes through imgproxy, sized for its place; the API sends only `sourceUrl`).
 */

/** A made profile file at `cssPx` wide, keeping its shape. An older API with no `sourceUrl` falls back to the file. */
export function profileThumbSrc(asset: SerializedProfileAsset, cssPx: number): string | null {
  if (asset.url === null || asset.fileRemoved) {
    return null;
  }
  // `typeof`: an older API answer has no `sourceUrl` at all.
  if (typeof asset.sourceUrl === 'string' && asset.sourceUrl !== '') {
    const proxied = imgproxySrc(asset.sourceUrl, cssPx, { square: false });
    if (proxied !== null) {
      return proxied;
    }
  }
  return profileFileUrl(asset);
}

/** The cover of the newest release (the ghost of an image that is not made), square, or null. */
export function releaseCoverSrc(release: ArtistProfileResponse['newestRelease'], cssPx: number): string | null {
  if (release === null || release.artworkUrl === null) {
    return null;
  }
  // A cover URL of a claim (a service CDN): through imgproxy when its host is allowed.
  if (/^https?:\/\//.test(release.artworkUrl)) {
    return drawnRemoteSrc(release.artworkUrl, cssPx);
  }
  // Our own file: its imgproxy source, else the file route of an older API.
  const source = release.artworkSourceUrl ?? null;
  if (source !== null && source !== '') {
    return imgproxySrc(source, cssPx) ?? getFullAPIFileURL(release.artworkUrl);
  }
  return getFullAPIFileURL(release.artworkUrl);
}

/** The round photo of the card: the Profile artist photo, else the Music24 artist photo, else null. */
export function profileAvatarSrc(
  profile: Pick<ArtistProfileResponse, 'assets' | 'avatar'>,
  cssPx: number
): string | null {
  const photo = profile.assets.find((one) => one.kind === 'spotifyAvatar')?.current ?? null;
  if (photo !== null && photo.status === 'ready') {
    const src = profileThumbSrc(photo, cssPx);
    if (src !== null) {
      return src;
    }
  }
  if (profile.avatar === null) {
    return null;
  }
  const { sourceUrl } = profile.avatar;
  if (sourceUrl !== undefined && sourceUrl !== '') {
    return imgproxySrc(sourceUrl, cssPx) ?? getFullAPIFileURL(profile.avatar.url);
  }
  return getFullAPIFileURL(profile.avatar.url);
}
